import { Listbox } from '@headlessui/react';
import styled from 'styled-components';

import { Box } from '@/shared/ui/box';
import { Caption } from '@/shared/ui/caption';

import { SelectProps, StyledAdornment, StyledScroll } from './select.types';

export const Relative = styled.div`
	position: relative;
`;

export const Select = styled(Listbox.Button)<
	Required<Pick<SelectProps<string>, 'variant' | 'width' | 'disabled'>> & StyledAdornment & { hasValue: boolean }
>`
	display: flex;
	align-items: center;
	outline: none;
	width: ${({ width, theme: { button } }) => button.size[width]};
	text-align: left;
	font-weight: 500;
	text-overflow: ellipsis;
	padding: ${({ adornment }) => (adornment ? '8px 16px 8px 42px' : '8px 16px')};
	border: 1.5px solid
		${({ variant, disabled, theme: { input } }) => (disabled ? input[variant].disabled.border : input[variant].border)};
	background-color: ${({ variant, theme: { input } }) => input[variant].background};
	color: ${({ variant, disabled, hasValue, theme: { input } }) =>
		disabled ? input[variant].disabled.color : hasValue ? input[variant].filled.color : input[variant].fontColor};
	font-size: 15px;
	border-radius: ${({ variant, theme: { input } }) => input[variant].radius};
	-moz-appearance: none;
	-webkit-appearance: none;
	appearance: none;
	padding-right: 40px;
	cursor: ${({ disabled }) => (disabled ? 'initial' : 'pointer')};
	white-space: nowrap;

	&[aria-expanded='true'] {
		border: 1.5px solid ${({ variant, theme: { input } }) => input[variant].focus.border};
		color: ${({ variant, theme: { input } }) => input[variant].focus.color};
	}
`;

export const Options = styled(Listbox.Options)<Required<Pick<SelectProps<string>, 'variant'>> & StyledScroll>`
	outline: none;
	width: 100%;
	position: absolute;
	margin-top: 8px;
	overflow-y: ${({ scroll }) => (scroll ? 'scroll' : 'hidden')};
	min-height: ${({ scroll }) => (scroll ? '230px' : 'hidden')};
	max-height: ${({ scroll }) => (scroll ? '230px' : 'hidden')};
	border: ${({ theme: { option } }) => option.border};
	border-radius: ${({ variant, theme: { input } }) => input[variant].radius};
	background-color: ${({ theme: { option } }) => option.background};
	overflow-x: hidden;
	z-index: ${({
		theme: {
			zIndex: { menu },
		},
	}) => menu};
`;

export const Option = styled(Listbox.Option)`
	padding: 8px 12px 8px 36px;
	outline: none;
	border: none;
	color: ${({ theme: { option } }) => option.color};
	background-color: ${({ theme: { option } }) => option.background};
	font-weight: 500;
	cursor: pointer;
	transition: ${({ theme: { transition } }) => transition.primary};

	&:hover {
		background-color: ${({ theme: { option } }) => option.hover};
	}
`;

export const Label = styled(Caption)`
	margin-bottom: 8px;

	& sup {
		color: ${({
			theme: {
				colors: { error },
			},
		}) => error};
	}
`;

export const Adornment = styled.div<
	Required<Pick<SelectProps<string>, 'variant'>> & Pick<SelectProps<string>, 'disabled'> & StyledAdornment
>`
	position: absolute;
	display: flex;
	z-index: ${({ theme: { zIndex } }) => zIndex.selectIcon};
	justify-content: center;
	align-items: center;
	border-right: 1.5px solid
		${({ variant, disabled, theme: { input } }) => (disabled ? input[variant].disabled.border : input[variant].border)};
	border-radius: ${({ variant, theme: { input } }) => input[variant].radius};
	top: 0;
	left: 0;
	height: 100%;
	width: 32px;
	border: ${({ open, variant, theme: { input } }) => open && `1.5px solid ${input[variant].focus.border}`};
`;

export const Selected = styled.div<{ selected?: boolean }>`
	position: relative;
	display: flex;
	align-items: center;
	color: ${({ selected, theme: { option } }) => (selected ? option.selectedColor : option.color)};
`;

export const LabelOption = styled.span`
	width: 100%;
	word-break: break-word;
`;

export const CheckIconWrapper = styled.span`
	position: absolute;
	left: -22px;
	top: 50%;
	transform: translateY(-50%);
	display: flex;
	align-items: center;
`;

export const SelectArrow = styled(Box)<{ open: boolean; disabled?: boolean }>`
	right: 18px;
	transform: rotate(${({ open }) => (open ? '180deg' : 0)});
	transition: transform 0.2s ease;

	& svg {
		fill: ${({ theme: { select }, disabled }) => {
			if (disabled) return select.fill.disabled;

			return select.fill.default;
		}};
	}
`;
